/**
 * Client bridge to the owen-apply edge function (OpenAI / Anthropic via Supabase secrets).
 */
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import {
  analyzeLegacyAnswer,
  analyzeV1Answer,
  analyzeFieldAnswer,
} from './owenAnalyzeAnswer';
import { defaultStepFromAllowed } from './applyNextSteps';
import { buildOwenApplyPayload } from './owenSanitizePayload';
import { sanitizeAiQuestionText } from '../config/owenApplyQuestionCatalog';

function mergeNextStep(validStep, rawStep, allowed) {
  const base = validStep || defaultStepFromAllowed(allowed);
  const aiQuestion = sanitizeAiQuestionText(rawStep?.question);
  if (aiQuestion && base.type === 'question') {
    return { ...base, question: aiQuestion };
  }
  return base;
}

/**
 * @param {object} params
 * @param {string} params.mode
 * @param {string} params.answeredQuestionId
 * @param {string} params.answeredLabel
 * @param {Record<string, string>} params.answers
 * @param {number} params.readinessScore
 * @param {Array} params.allowedNextSteps
 * @param {Array} [params.recommendedPrograms]
 * @param {Array} [params.transcript]
 */
export async function fetchOwenApplyGuidance(params) {
  const fallback = () => rulesFallback(params);

  if (!isSupabaseConfigured) {
    return fallback();
  }

  try {
    const { data, error } = await supabase.functions.invoke('owen-apply', {
      body: buildOwenApplyPayload(params),
    });

    if (error) throw error;

    const insight = String(data?.insight || '').trim();
    const nextStep = data?.nextStep;
    const allowed = params.allowedNextSteps || [];
    const validStep = allowed.find(
      (s) => s.type === nextStep?.type && s.id === nextStep?.id,
    );
    const mergedStep = mergeNextStep(validStep, nextStep, allowed);

    return {
      insight: insight || rulesFallback(params).insight,
      nextStep: mergedStep,
      model: data?.model || 'unknown',
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[owen-apply] fallback:', e?.message || e);
    return fallback();
  }
}

function rulesFallback(params) {
  const { mode, answeredQuestionId, answers, readinessScore, allowedNextSteps } = params;
  let insight = `Your readiness is ${readinessScore}/100 so far.`;
  if (mode === 'v1-triage') insight = analyzeV1Answer(answeredQuestionId, answers);
  else if (mode === 'v1-field') insight = analyzeFieldAnswer(answeredQuestionId);
  else insight = analyzeLegacyAnswer(answeredQuestionId, answers);

  return {
    insight,
    nextStep: defaultStepFromAllowed(allowedNextSteps),
    model: 'rules-fallback',
  };
}

export const OWEN_WELCOME_FALLBACK = `Hi — I'm Owen, your guide on LoanKnow.

I'll ask a few simple questions about your goals and the property. There are no trick questions, and you can tap any answer to change it or start over anytime.

LoanKnow moves in four parts: we find the right loan program for you, then help you build an application shaped to that program, then an underwriting review, and finally connecting with a lender when you are ready. On my panel to the right you can always see where you are on that path — we are starting with program match.

This is preparation, not a loan approval. I'm here to help you get ready — not to decide whether you qualify.

Your sensitive details stay protected. Information like your Social Security number, full street address, email, and phone never leaves your LoanKnow file — when we ask for those, you'll see them listed under "What I don't see" on my panel.

Ready when you are — let's start with an easy one.`;

export async function fetchOwenApplyWelcome() {
  if (!isSupabaseConfigured) {
    return { message: OWEN_WELCOME_FALLBACK, model: 'rules-fallback' };
  }

  try {
    const { data, error } = await supabase.functions.invoke('owen-apply', {
      body: { mode: 'welcome' },
    });
    if (error) throw error;
    const message = String(data?.message || '').trim();
    return {
      message: message.length >= 40 ? message : OWEN_WELCOME_FALLBACK,
      model: data?.model || 'unknown',
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[owen-apply] welcome fallback:', e?.message || e);
    return { message: OWEN_WELCOME_FALLBACK, model: 'rules-fallback' };
  }
}
